import React, { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isNil from 'lodash/isNil';
import parseDate from 'date-fns/parse';
import isBefore from 'date-fns/isBefore';

import { SuiCheckbox, SuiLabel } from '@splashthat/sui';
import { getChildrenFromNodeIdByInputType } from 'common/entities/formNode/selectors';
import {
    DATE_OPTION,
    DATE_MAX,
    DATE_MIN,
    DATE_DURATION_VALUE,
    DATE_DURATION_UNIT,
    TIME_MAX,
    TIME_MIN,
    DATE_OPTION_DURATION,
    DATE_OPTION_DATES,
    DATE_DURATION_UNIT_DAYS,
    USE_BROWSER_TIME,
} from 'common/entities/formNode/nodeConfig';
import { INPUT_TYPE_DATE, INPUT_TYPE_TIME } from 'common/entities/formNode/inputTypes';

import { updateFormNodeConfigValue } from '../../../actions/nodeActions';
import { createActionUpsertLiveErrors } from '../../../actions';
import { getEditFormNodeId, getMultipleFormNodesConfig } from '../../../selectors';
import { INVALID_DATES, INVALID_DURATION_VALUE, INVALID_TIMES } from '../../../constants/errorCodes';
import RequiredControl from '../components/RequiredControl';
import { DateRangeControlUI } from '../components/DateRangeControl';
import TimeRangeControl from './TimeRangeControl';
import DateDuration from './DateDuration';
import { DateRangeContainer, DateRangeSection, DateRangeError, TimeRangeError } from './index.styles';

const AVAILABLE_DAY_LABEL = 'First Available Date';
const CHECKBOX_LABEL = 'Set relative to submission date';
const DATE_RANGE_LABEL = 'Available Dates';
const TIME_RANGE_LABEL = 'Available Times';
const REQUIRED_ERROR_MESSAGE = 'Required';
const DATES_ORDER_ERROR_MESSAGE = 'Start date must be before end date';
const TIMES_ORDER_ERROR_MESSAGE = 'Start time must be before end time';
const APPLY_VALIDATION_TO_TIME = 'Apply this validation to time';

const useNodesUpdateCallback = (nodes, configKey) => {
    const dispatch = useDispatch();

    return useCallback(
        (dateTo) => nodes.forEach((node) => dispatch(updateFormNodeConfigValue(node.id, configKey, dateTo))),
        [dispatch, nodes],
    );
};

const validateDateRange = (dateOption, datesFrom, datesTo, dateDurationValue, dateDurationUnit, timesFrom, timesTo) => {
    const parsedDatesTo = parseDate(datesTo, 'MM/dd/yyyy', new Date());
    const parsedDatesFrom = parseDate(datesFrom, 'MM/dd/yyyy', new Date());
    const parsedTimesTo = parseDate(timesTo, 'h:mma', new Date());
    const parsedTimesFrom = parseDate(timesFrom, 'h:mma', new Date());

    return {
        [INVALID_DURATION_VALUE]:
            dateOption === DATE_OPTION_DURATION && (isNil(dateDurationValue) || dateDurationValue < 0),
        [INVALID_DATES]: dateOption !== DATE_OPTION_DURATION && isBefore(parsedDatesTo, parsedDatesFrom),
        [INVALID_TIMES]: isBefore(parsedTimesTo, parsedTimesFrom),
    };
};

const DateRange = (props) => {
    const dispatch = useDispatch();

    const formNodeId = useSelector(getEditFormNodeId);
    const dateChildren = useSelector((state) => getChildrenFromNodeIdByInputType(state, formNodeId, INPUT_TYPE_DATE));
    const timeChildren = useSelector((state) => getChildrenFromNodeIdByInputType(state, formNodeId, INPUT_TYPE_TIME));

    const dateOption = useSelector((state) => getMultipleFormNodesConfig(state, dateChildren, DATE_OPTION));
    const datesFrom = useSelector((state) => getMultipleFormNodesConfig(state, dateChildren, DATE_MIN));
    const datesTo = useSelector((state) => getMultipleFormNodesConfig(state, dateChildren, DATE_MAX));
    const dateDurationValue = useSelector((state) =>
        getMultipleFormNodesConfig(state, dateChildren, DATE_DURATION_VALUE),
    );
    const dateDurationUnit = useSelector((state) =>
        getMultipleFormNodesConfig(state, dateChildren, DATE_DURATION_UNIT),
    );
    const timesFrom = useSelector((state) => getMultipleFormNodesConfig(state, timeChildren, TIME_MIN));
    const timesTo = useSelector((state) => getMultipleFormNodesConfig(state, timeChildren, TIME_MAX));
    const useBrowserTime = useSelector((state) => getMultipleFormNodesConfig(state, timeChildren, USE_BROWSER_TIME));

    const setDateOption = useNodesUpdateCallback(dateChildren, DATE_OPTION);
    const setDatesTo = useNodesUpdateCallback(dateChildren, DATE_MAX);
    const setDatesFrom = useNodesUpdateCallback(dateChildren, DATE_MIN);
    const setDateDurationValue = useNodesUpdateCallback(dateChildren, DATE_DURATION_VALUE);
    const setDateDurationUnit = useNodesUpdateCallback(dateChildren, DATE_DURATION_UNIT);
    const setTimesTo = useNodesUpdateCallback(timeChildren, TIME_MAX);
    const setTimesFrom = useNodesUpdateCallback(timeChildren, TIME_MIN);
    const setUseBrowserTime = useNodesUpdateCallback(timeChildren, USE_BROWSER_TIME);

    const errors = validateDateRange(
        dateOption,
        datesFrom,
        datesTo,
        dateDurationValue,
        dateDurationUnit,
        timesFrom,
        timesTo,
    );

    useEffect(() => {
        dispatch(createActionUpsertLiveErrors(errors));
    }, [dateOption, datesFrom, datesTo, dateDurationValue, timesFrom, timesTo]);

    return (
        <div>
            <RequiredControl disabled={false} />
            <DateRangeContainer>
                <DateRangeSection>
                    <SuiLabel>{DATE_RANGE_LABEL}</SuiLabel>
                    <SuiCheckbox
                        checked={dateOption === DATE_OPTION_DURATION}
                        onChange={(_, value) => {
                            if (dateOption === DATE_OPTION_DURATION) {
                                setDateOption(DATE_OPTION_DATES);
                                return;
                            }

                            setDateOption(DATE_OPTION_DURATION);
                            !dateDurationValue && setDateDurationValue(1);
                            !dateDurationUnit && setDateDurationUnit(DATE_DURATION_UNIT_DAYS);
                        }}
                        label={CHECKBOX_LABEL}
                    />
                    {dateOption === DATE_OPTION_DURATION ? (
                        <React.Fragment>
                            <SuiLabel>{AVAILABLE_DAY_LABEL}</SuiLabel>
                            <DateDuration
                                error={errors[INVALID_DURATION_VALUE]}
                                dateDurationValue={dateDurationValue}
                                dateDurationUnit={dateDurationUnit}
                                setDateDurationValue={(value) => {
                                    value !== 0 && setUseBrowserTime(false);
                                    setDateDurationValue(value);
                                }}
                                setDateDurationUnit={setDateDurationUnit}
                            />
                            {dateDurationValue === 0 && (
                                <SuiCheckbox
                                    checked={useBrowserTime}
                                    onChange={(_, value) => setUseBrowserTime(value)}
                                    label={APPLY_VALIDATION_TO_TIME}
                                />
                            )}
                            {errors[INVALID_DURATION_VALUE] && (
                                <DateRangeError>{REQUIRED_ERROR_MESSAGE}</DateRangeError>
                            )}
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <DateRangeControlUI
                                dateMax={datesTo}
                                dateMin={datesFrom}
                                onChangeDateMax={setDatesTo}
                                onChangeDateMin={setDatesFrom}
                                error={errors[INVALID_DATES]}
                                disabled={false}
                            />
                            {errors[INVALID_DATES] && <DateRangeError>{DATES_ORDER_ERROR_MESSAGE}</DateRangeError>}
                        </React.Fragment>
                    )}
                </DateRangeSection>
                <DateRangeSection>
                    <SuiLabel>{TIME_RANGE_LABEL}</SuiLabel>
                    <TimeRangeControl
                        timeTo={timesTo}
                        timeFrom={timesFrom}
                        onChangeTimeTo={setTimesTo}
                        onChangeTimeFrom={setTimesFrom}
                        error={errors[INVALID_TIMES]}
                    />
                    {errors[INVALID_TIMES] && <TimeRangeError>{TIMES_ORDER_ERROR_MESSAGE}</TimeRangeError>}
                </DateRangeSection>
            </DateRangeContainer>
        </div>
    );
};

export default React.memo(DateRange);
